import { combineReducers } from 'redux';
import { applyCombineReducersByKey } from 'common/util/applyCombineReducersByKey';
import { descriptor as descriptorReducer } from './descriptor';
import lodash from 'lodash';

import {
    INDEX_DOCUMENT_DOCUMENT_PERMISSION,
    INDEX_DOCUMENT_UNLOAD,
    INDEX_DOCUMENT_RESET,
    INDEX_DOCUMENT_RESTORE,
    INDEX_DOCUMENT_WRITE_RESTORE,
    INDEX_DOCUMENT_ASCERTAIN_LOADING,
    INDEX_DOCUMENT_ASCERTAIN_RESULT,
    INDEX_DOCUMENT_SAVESTART,
    INDEX_DOCUMENT_SAVEFAILED,
    INDEX_DOCUMENT_SAVECOMPLETE,
    INDEX_DOCUMENT_NOTIFY_LOADING,
    INDEX_DOCUMENT_LOAD_DOCUMENT,
    INDEX_DOCUMENT_FAILED_LOAD_DOCUMENT,
    INDEX_DOCUMENT_LOAD_DESCRIPTORS,
    INDEX_DOCUMENT_CHANGE_DOCUMENT_TYPES_VISIBILITY,
    INDEX_DOCUMENT_CHANGE_DOCUMENT_TYPE_FILTER,
    INDEX_DOCUMENT_SELECT_DOCUMENT_TYPE,
    INDEX_DOCUMENT_DESELECT_DOCUMENT_TYPE,
    INDEX_DOCUMENT_HIGHLIGHT_DOCUMENT_TYPE,
    UPDATE_TRIGGERING_SENDING_DOCUMENT_STATUS
} from 'indexDocuments/actions';

function documentPermission(state = null, action) {
    if (action.type === INDEX_DOCUMENT_DOCUMENT_PERMISSION) {
        return action.payload;
    }
    return state;
}

function loading(state = true, action) {
    if (action.type === INDEX_DOCUMENT_NOTIFY_LOADING) {
        return true;
    }
    if (action.type === INDEX_DOCUMENT_LOAD_DOCUMENT) {
        return false;
    }
    if (action.type === INDEX_DOCUMENT_FAILED_LOAD_DOCUMENT) {
        return false;
    }
    return state;
}

function loaded(state = false, action) {
    if (action.type === INDEX_DOCUMENT_NOTIFY_LOADING) {
        return false;
    }
    if (action.type === INDEX_DOCUMENT_LOAD_DOCUMENT) {
        return true;
    }
    return state;
}

function failedToLoad(state = false, action) {
    if (action.type === INDEX_DOCUMENT_NOTIFY_LOADING) {
        return false;
    }
    if (action.type === INDEX_DOCUMENT_FAILED_LOAD_DOCUMENT) {
        return true;
    }
    return state;
}

function binder(state = null, action) {
    if (action.type === INDEX_DOCUMENT_LOAD_DOCUMENT) {
        const { binder } = action.payload;
        return lodash.cloneDeep(binder);
    }
    return state;
}

function indexingType(state = null, action) {
    if (action.type === INDEX_DOCUMENT_LOAD_DOCUMENT) {
        return action.payload.indexingType;
    }
    return state;
}

function binderIndexingMetadata(state = {}, action) {
    if (action.type === INDEX_DOCUMENT_LOAD_DOCUMENT) {
        return action.payload.binderIndexingMetadata;
    }
    return state;
}

function document(state = null, action) {
    if (action.type === INDEX_DOCUMENT_LOAD_DOCUMENT) {
        const { document } = action.payload;
        return lodash.cloneDeep(document);
    }
    if (action.type === INDEX_DOCUMENT_FAILED_LOAD_DOCUMENT) {
        return null;
    }
    return state;
}

function documentTypes(state = {}, action) {
    if (action.type === INDEX_DOCUMENT_LOAD_DOCUMENT) {
        const { documentTypes } = action.payload;
        return lodash.keyBy(documentTypes, 'id');
    }
    return state;
}

function selectedDocumentTypeId(state = null, action) {
    if (action.type === INDEX_DOCUMENT_LOAD_DOCUMENT) {
        const { documentTypeId } = action.payload;
        return documentTypeId || null;
    }
    if (action.type === INDEX_DOCUMENT_SELECT_DOCUMENT_TYPE) {
        return action.payload;
    }
    if (action.type === INDEX_DOCUMENT_DESELECT_DOCUMENT_TYPE) {
        return null;
    }
    if (action.type === INDEX_DOCUMENT_RESET) {
        return action.payload.documentTypeId || state;
    }
    if (action.type === INDEX_DOCUMENT_RESTORE) {
        return action.payload.documentTypeId || state;
    }
    return state;
}

function originalDocumentTypeId(state = null, action) {
    if (action.type === INDEX_DOCUMENT_LOAD_DOCUMENT) {
        const { document } = action.payload;
        return lodash.get(document, 'documentType.id', null);
    }
    return state;
}

function restoreDocumentTypeId(state = null, action) {
    if (action.type === INDEX_DOCUMENT_WRITE_RESTORE) {
        const { documentTypeId } = action.payload;
        return documentTypeId || state;
    }
    return state;
}

function selectedDescriptors(state = {}, action) {
    if (action.type === INDEX_DOCUMENT_LOAD_DESCRIPTORS) {
        const { descriptors } = action.payload;
        return lodash.keyBy(descriptors, 'id');
    }
    if (action.type === INDEX_DOCUMENT_SELECT_DOCUMENT_TYPE) {
        return {};
    }
    if (action.type === INDEX_DOCUMENT_DESELECT_DOCUMENT_TYPE) {
        return {};
    }
    if (action.type === INDEX_DOCUMENT_RESET) {
        return action.payload.descriptors || state;
    }
    if (action.type === INDEX_DOCUMENT_RESTORE) {
        return action.payload.descriptors || state;
    }
    return state;
}

function originalSelectedDescriptors(state = {}, action) {
    if (action.type === INDEX_DOCUMENT_LOAD_DESCRIPTORS && lodash.isEmpty(state)) {
        const { descriptors } = action.payload;
        return lodash.keyBy(descriptors, 'id');
    }
    if (action.type === INDEX_DOCUMENT_DESELECT_DOCUMENT_TYPE) {
        return {};
    }
    return state;
}

function restoreSelectedDescriptors(state = {}, action) {
    if (action.type === INDEX_DOCUMENT_WRITE_RESTORE) {
        const { descriptors } = action.payload;
        return descriptors || state;
    }
    return state;
}

function getStructuredValues(values) {
    if (!values) return [];
    return values
        .filter(value => !lodash.isEmpty(value))
        .map(value => ({
            value,
            originalValue: value,
            editorValue: value,
            invalidValue: false,
            editMode: false,
        }));

}

function descriptorValues(state = {}, action) {
    if (action.type === INDEX_DOCUMENT_LOAD_DOCUMENT) {
        const { document: { descriptors } } = action.payload;
        return lodash.chain(descriptors)
            .map(descriptor => {
                const { values } = descriptor;
                const structuredValues = getStructuredValues(values);
                return {
                    id: descriptor.id,
                    editorValue: '',
                    invalidValue: false,
                    highlightedIndex: -1,
                    touched: false,
                    values: structuredValues,
                };
            })
            .keyBy('id')
            .value();
    }
    if (action.type === INDEX_DOCUMENT_LOAD_DESCRIPTORS) {
        const { descriptors, binderIndexingMetadata } = action.payload;
        const newDescriptors = lodash.chain(descriptors)
            .values()
            .differenceBy(state, 'id')
            .reduce((newDescriptors, descriptor) => {
                const descriptorValues = descriptor.values || [];
                const values = lodash.isEmpty(descriptorValues) && lodash.has(binderIndexingMetadata.defaultValues, descriptor.id)
                    ? [binderIndexingMetadata.defaultValues[descriptor.id]]
                    : descriptorValues;
                const structuredValues = getStructuredValues(values);
                newDescriptors[descriptor.id] = {
                    id: descriptor.id,
                    editorValue: '',
                    invalidValue: false,
                    highlightedIndex: -1,
                    touched: false,
                    values: structuredValues,
                };
                return newDescriptors;
            }, {})
            .value();
        return {
            ...newDescriptors,
            ...state,
        };
    }
    if (action.type === INDEX_DOCUMENT_RESET) {
        return action.payload.descriptorValues || state;
    }
    if (action.type === INDEX_DOCUMENT_RESTORE) {
        return action.payload.descriptorValues || state;
    }
    return applyCombineReducersByKey(descriptorReducer, 'payload.descriptorId')(state, action);
}

function originalDescriptorValues(state = {}, action) {
    if (action.type === INDEX_DOCUMENT_LOAD_DOCUMENT) {
        const { document: { descriptors } } = action.payload;
        return lodash.chain(descriptors)
            .map(descriptor => {
                const { values } = descriptor;
                const structuredValues = getStructuredValues(values);
                return {
                    id: descriptor.id,
                    editorValue: '',
                    invalidValue: false,
                    highlightedIndex: -1,
                    touched: false,
                    values: structuredValues
                };
            })
            .keyBy('id')
            .value();
    }
    if (action.type === INDEX_DOCUMENT_LOAD_DESCRIPTORS) {
        const { descriptors, binderIndexingMetadata } = action.payload;
        const newDescriptors = lodash.chain(descriptors)
            .values()
            .differenceBy(state, 'id')
            .reduce((newDescriptors, descriptor) => {
                const descriptorValues = descriptor.values || [];
                const values = lodash.isEmpty(descriptorValues) && lodash.has(binderIndexingMetadata.defaultValues, descriptor.id)
                    ? [binderIndexingMetadata.defaultValues[descriptor.id]]
                    : descriptorValues;
                const structuredValues = getStructuredValues(values);
                newDescriptors[descriptor.id] = {
                    id: descriptor.id,
                    editorValue: '',
                    invalidValue: false,
                    highlightedIndex: -1,
                    touched: false,
                    values: structuredValues,
                };
                return newDescriptors;
            }, {})
            .value();
        return {
            ...newDescriptors,
            ...state,
        };
    }
    return state;
}

function restoreDescriptorValues(state = {}, action) {
    if (action.type === INDEX_DOCUMENT_WRITE_RESTORE) {
        const { descriptorValues } = action.payload;
        return descriptorValues || state;
    }
    return state;
}

function documentTypesVisibility(state = false, action) {
    if (action.type === INDEX_DOCUMENT_CHANGE_DOCUMENT_TYPES_VISIBILITY) {
        return !!action.payload;
    }
    if (action.type === INDEX_DOCUMENT_RESET) {
        return false;
    }
    return state;
}

function documentTypeFilter(state = '', action) {
    if (action.type === INDEX_DOCUMENT_CHANGE_DOCUMENT_TYPE_FILTER) {
        return action.payload;
    }
    if (action.type === INDEX_DOCUMENT_RESET) {
        return '';
    }
    return state;
}

function highlightedDocumentTypeId(state = null, action) {
    if (action.type === INDEX_DOCUMENT_HIGHLIGHT_DOCUMENT_TYPE) {
        return action.payload;
    }
    if (action.type === INDEX_DOCUMENT_RESET) {
        return null;
    }
    return state;
}

function saving(state = false, action) {
    if (action.type === INDEX_DOCUMENT_SAVESTART) {
        return true;
    }
    if (action.type === INDEX_DOCUMENT_SAVECOMPLETE) {
        return false;
    }
    if (action.type === INDEX_DOCUMENT_SAVEFAILED) {
        return false;
    }
    return state;
}

function saveComplete(state = false, action) {
    if (action.type === INDEX_DOCUMENT_SAVECOMPLETE) {
        return true;
    }
    return state;
}

function canRestore(state = false, action) {
    if (action.type === INDEX_DOCUMENT_WRITE_RESTORE) {
        const { documentTypeId } = action.payload;
        return !lodash.isEmpty(documentTypeId);
    }
    return state;
}

function ascertainmentLoading(state = false, action) {
    if (action.type === INDEX_DOCUMENT_ASCERTAIN_LOADING) {
        return action.payload;
    }
    return state;
}

function ascertainmentResult(state = null, action) {
    if (action.type === INDEX_DOCUMENT_ASCERTAIN_RESULT) {
        return action.payload;
    }
    return state;
}

function redirectToSdfa(state = false, action) {
    if(action.type === INDEX_DOCUMENT_SAVECOMPLETE){
        return action.payload;
    }
    return state;
}

function triggeredSendingDocument(state = false, action) {
    if (action.type === UPDATE_TRIGGERING_SENDING_DOCUMENT_STATUS) {
        return action.payload;
    }

    return state;
}

export const indexDocument = combineReducers({
    documentPermission,
    loading,
    loaded,
    failedToLoad,
    binder,
    indexingType,
    document,
    documentTypes,
    selectedDocumentTypeId,
    originalDocumentTypeId,
    restoreDocumentTypeId,
    selectedDescriptors,
    originalSelectedDescriptors,
    restoreSelectedDescriptors,
    descriptorValues,
    originalDescriptorValues,
    restoreDescriptorValues,
    documentTypesVisibility,
    documentTypeFilter,
    highlightedDocumentTypeId,
    saving,
    saveComplete,
    canRestore,
    ascertainmentLoading,
    ascertainmentResult,
    binderIndexingMetadata,
    redirectToSdfa,
    triggeredSendingDocument,
});

const indexDocumentActionMatcher = /^INDEX_DOCUMENT_/;
export default function indexDocumentReducer(state, action) {
    if (!indexDocumentActionMatcher.test(action.type) && action.type !== '@@INIT') {
        return state;
    }
    if (action.type === INDEX_DOCUMENT_UNLOAD) {
        return {
            ...state,
            indexDocument: indexDocument({}, action)
        };
    }
    return {
        ...state,
        indexDocument: indexDocument(state.indexDocument, action)
    };
}
